"use client";

import React, { useEffect, useRef, useState } from "react";
import { ShieldCheck, Stethoscope, Activity, Award, CircleCheck } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const roles = [{
  id: "csa",
  abbr: "CSA",
  name: "Certified Surgical Assistant",
  desc: "Specialized professionals providing crucial aid during surgical procedures, ensuring optimal patient outcomes.",
  icon: ShieldCheck
}, {
  id: "csfa",
  abbr: "CSFA",
  name: "Certified Surgical First Assistant",
  desc: "Advanced assistants skilled in tissue handling, suturing, and hemostasis under direct surgeon supervision.",
  icon: Stethoscope
}, {
  id: "rnfa",
  abbr: "RNFA",
  name: "Registered Nurse First Assistant",
  desc: "Perioperative nurses with advanced education and training for direct, hands-on surgical assistance.",
  icon: Activity
}, {
  id: "pa-c",
  abbr: "PA-C",
  name: "Physician Assistant - Certified",
  desc: "Highly qualified medical providers licensed to diagnose, treat, and assist in complex surgical interventions.",
  icon: Award
}];
const standards = ["Primary Source Verification", "Current State Licensure", "BLS / ACLS Certification", "Comprehensive Background Checks", "Immunization & Health Clearance", "Annual Skills Competency"];
export default function CredentialCertification() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="credentialcertification" ref={sectionRef} className="py-24 bg-background overflow-hidden">
            <div className="container mx-auto px-4 md:px-6">
                {}
                <div className={`max-w-3xl mx-auto text-center transition-all duration-700 ease-out ${isSectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                    <h2 className="text-3xl md:text-4xl font-bold text-primary tracking-tight">
                        Qualified & Credentialed Professionals
                    </h2>
                    
                    {}
                    <div className="h-1 w-16 bg-accent-foreground mx-auto my-6 rounded-full" />
                    
                    <p className="text-lg text-muted-foreground leading-relaxed">
                        At Atlas Surgical Partners, we maintain the highest standards of clinical excellence. 
                        Every professional in our network undergoes rigorous vetting to ensure complete adherence 
                        to facility protocols and regulatory requirements.
                    </p>
                </div>

                {}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
                    {roles.map((role, i) => {
          const Icon = role.icon;
          return <div key={role.id} data-index={i} className={`transition-all duration-700 ease-out delay-[${i * 100}ms] ${isSectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${i * 100}ms`
          }}>
                                <Card className="text-card-foreground flex flex-col gap-6 py-6 h-full rounded-xl border-border/40 transition-shadow bg-card relative overflow-hidden group !shadow-[0px_4px_8px_rgba(20,21,31,0.18),_0px_2px_4px_rgba(20,21,31,0.13)] hover:shadow-md">
                                    {}
                                    <div className="absolute top-0 left-0 w-full h-1 bg-accent-foreground transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out" />
                                    
                                    <CardHeader className="pb-4">
                                        <div className="w-12 h-12 rounded-lg bg-primary/5 flex items-center justify-center mb-4 text-accent-foreground">
                                            <Icon className="w-6 h-6" />
                                        </div>
                                        <CardTitle className="text-primary flex items-baseline gap-2">
                                            <span className="text-2xl font-bold">{role.abbr}</span>
                                        </CardTitle>
                                        <CardDescription className="text-sm font-medium text-primary/80">
                                            {role.name}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-muted-foreground text-sm leading-relaxed">
                                            {role.desc}
                                        </p>
                                    </CardContent>
                                </Card>
                            </div>;
        })}

                    {}
                    <div data-index={roles.length} className={`md:col-span-2 lg:col-span-4 transition-all duration-700 ease-out delay-500 ${isSectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                        <Card className="rounded-xl border-border/40 shadow-sm bg-card mt-2">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-xl text-primary flex items-center gap-2">
                                    <ShieldCheck className="w-5 h-5 text-accent-foreground" />
                                    Comprehensive Licensure & Vetting Standards
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Separator className="mb-6 opacity-50" />
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-4 gap-x-8">
                                    {standards.map((standard, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                                            <CircleCheck className="w-5 h-5 text-accent-foreground shrink-0 mt-0.5" />
                                            <span className="text-sm text-foreground font-medium">
                                                {standard}
                                            </span>
                                        </div>)}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </section>;
}